import { useParams, Link } from "react-router-dom";
import { Truck, MapPin, CheckCircle, ChevronRight, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";

const stateData = {
  massachusetts: {
    name: "Massachusetts",
    abbr: "MA",
    cities: ["Boston", "Worcester", "Springfield", "Cambridge", "Lowell", "Brockton", "New Bedford", "Fall River"],
    description: "Serving the Greater Boston area and all of Massachusetts with reliable dry van freight transportation.",
    nearby: ["New Hampshire", "Rhode Island", "Connecticut", "New York"],
  },
  "new-hampshire": {
    name: "New Hampshire",
    abbr: "NH", 
    cities: ["Manchester", "Nashua", "Concord", "Dover", "Rochester", "Salem", "Merrimack", "Londonderry"],
    description: "Fast and efficient trucking services throughout New Hampshire, from Manchester to the White Mountains.",
    nearby: ["Massachusetts", "Maine", "Vermont"],
  },
  maine: {
    name: "Maine",
    abbr: "ME",
    cities: ["Portland", "Lewiston", "Bangor", "South Portland", "Auburn", "Biddeford", "Sanford", "Augusta"],
    description: "Connecting Maine businesses to markets across the Northeast and nationwide.",
    nearby: ["New Hampshire", "Massachusetts"],
  },
  "rhode-island": {
    name: "Rhode Island",
    abbr: "RI",
    cities: ["Providence", "Warwick", "Cranston", "Pawtucket", "East Providence", "Woonsocket", "Newport", "Central Falls"],
    description: "Comprehensive freight solutions for Rhode Island's thriving business community.",
    nearby: ["Massachusetts", "Connecticut"],
  },
};

const StatePage = () => {
  const { state } = useParams();
  const data = stateData[state];

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="heading-2 text-slate-900 mb-4">State Not Found</h1>
          <Link to="/">
            <Button>Back to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const benefits = [
    "Same-day pickup available",
    "Competitive rates",
    "Real-time GPS tracking",
    "Full cargo insurance",
    "Professional drivers",
    "24/7 dispatch support",
  ];

  return (
    <div data-testid={`state-page-${state}`}>
      <SEOHead 
        title={`Trucking Company in ${data.name} | KDA Trucking LLC`}
        description={`Professional dry van trucking services in ${data.name}. Serving ${data.cities.slice(0, 4).join(', ')} and more. Reliable freight transportation. Call (617) 913-2934 for free quote!`}
      />
      {/* Hero Section */}
      <section className="bg-slate-900 py-20">
        <div className="section-container">
          <p className="text-orange-500 font-bold uppercase tracking-widest mb-4 text-sm">
            Dry Van Trucking Services
          </p>
          <h1 className="heading-1 text-white mb-6">
            Trucking Company in<br />
            <span className="text-orange-500">{data.name}</span>
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl mb-8">
            {data.description} KDA Trucking LLC provides professional dry van freight 
            transportation to businesses throughout {data.name} and the entire United States.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/request-service">
              <Button className="bg-orange-600 hover:bg-orange-700 text-white rounded-sm font-bold uppercase tracking-wider px-8 py-6 text-base">
                Get a Free Quote
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <a href="tel:+16179132934">
              <Button variant="outline" className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-slate-900 rounded-sm font-bold uppercase tracking-wider px-8 py-6 text-base">
                <Phone className="mr-2 h-5 w-5" />
                (617) 913-2934
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Cities Served */}
      <section className="py-20 bg-slate-50">
        <div className="section-container">
          <div className="text-center mb-12">
            <h2 className="heading-2 text-slate-900 mb-4">
              Cities We Serve in {data.name}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              We provide pickup and delivery services to all major cities in {data.name}.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {data.cities.map((city, index) => (
              <div 
                key={index}
                className="bg-white p-6 border border-slate-200 flex items-center gap-3"
              >
                <MapPin className="h-5 w-5 text-orange-600 flex-shrink-0" />
                <span className="font-medium text-slate-900">{city}, {data.abbr}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-orange-600 font-bold uppercase tracking-widest mb-4 text-sm">
                Our Services
              </p>
              <h2 className="heading-2 text-slate-900 mb-6">
                {data.name} Freight Services
              </h2>
              <p className="text-slate-600 text-lg mb-8">
                Whether you need to ship within {data.name} or across the country, 
                KDA Trucking LLC has you covered. Our dry van trucks are perfect for:
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-orange-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">General freight and merchandise</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-orange-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Palletized goods and boxed cargo</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-orange-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Retail and e-commerce shipments</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-orange-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Manufacturing and industrial supplies</span>
                </li>
              </ul>
              <Link to="/request-service">
                <Button className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm font-bold uppercase tracking-wider px-8 py-4">
                  Request a Quote
                </Button>
              </Link>
            </div>
            <div className="bg-slate-100 p-8">
              <h3 className="heading-3 text-slate-900 mb-6">Why Choose KDA Trucking?</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-orange-600 flex-shrink-0" />
                    <span className="text-slate-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Nearby States */}
      <section className="py-20 bg-slate-50">
        <div className="section-container">
          <div className="text-center mb-12">
            <h2 className="heading-2 text-slate-900 mb-4">
              Also Serving Nearby States
            </h2>
            <p className="text-slate-600">
              We provide freight transportation throughout New England and beyond.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            {data.nearby.map((nearbyState, index) => (
              <Link
                key={index}
                to={`/trucking/${nearbyState.toLowerCase().replace(' ', '-')}`}
                className="bg-white px-6 py-4 border border-slate-200 hover:border-orange-500 transition-colors font-medium text-slate-900"
              >
                {nearbyState} Trucking Services
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-900 py-20">
        <div className="section-container text-center">
          <h2 className="heading-2 text-white mb-6">
            Need Freight Services in {data.name}?
          </h2>
          <p className="text-slate-400 text-lg mb-8 max-w-2xl mx-auto">
            Get a free quote today. We offer competitive rates and reliable service 
            for all your trucking needs in {data.name} and nationwide.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/request-service">
              <Button className="bg-orange-600 hover:bg-orange-700 text-white rounded-sm font-bold uppercase tracking-wider px-8 py-6 text-base">
                Get Free Quote
              </Button>
            </Link>
            <a href="tel:+16179132934">
              <Button variant="outline" className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-slate-900 rounded-sm font-bold uppercase tracking-wider px-8 py-6 text-base">
                Call (617) 913-2934
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StatePage;
